<template>
  <div class="legal-page">
    <PageBanner
      title="Privacy Policy"
      subtitle="How Clients Preferred Property Hub collects, uses and protects your information."
    />

    <LegalSection
      updated="28 July 2026"
      jurisdiction="Governed by the laws of India"
    >
      <div class="callout">
        <p>
          In short: we only collect what you send us through the enquiry form or
          by contacting us directly, we use it to respond to your property
          enquiry, and we do not sell it to anyone.
        </p>
      </div>

      <h2>1. Who we are</h2>
      <p>
        Clients Preferred Property Hub is a real estate consultancy operating in
        the Tricity region, with its office at {{ site.address.street }},
        {{ site.address.locality }}, {{ site.address.region }}
        {{ site.address.postalCode }}. In this policy, "we", "us" and "our"
        refer to Clients Preferred Property Hub, and "you" refers to any visitor
        to this website. We are the data fiduciary for the personal data
        described below.
      </p>

      <h2>2. What we collect</h2>
      <h3>Information you give us</h3>
      <p>
        When you submit the enquiry form on our Contact page, we collect your
        name, email address, phone number, the subject you select and the
        message you write. If you call, message or email us directly, we hold
        whatever you choose to share in that conversation.
      </p>

      <h3>Information collected automatically</h3>
      <p>
        This website does not run its own analytics or advertising trackers. Our
        hosting provider may keep standard server logs, which can include your
        IP address, browser type and the pages requested, for security and
        troubleshooting. Cookies and similar technologies are described in our
        <router-link to="/cookie-policy">Cookie Policy</router-link>.
      </p>

      <h2>3. Why we use it</h2>
      <ul>
        <li>To respond to your enquiry and discuss suitable properties.</li>
        <li>
          To share project details, pricing and availability that match what you
          asked for.
        </li>
        <li>
          To follow up on an ongoing conversation about a site visit, booking or
          documentation.
        </li>
        <li>
          To keep records of our dealings with you and meet our legal and
          regulatory obligations.
        </li>
        <li>
          To protect the website against misuse, spam and security incidents.
        </li>
      </ul>
      <p>
        We do not use your details for automated decision making or profiling,
        and we do not send bulk marketing to people who have not asked for it.
      </p>

      <h2>4. Who we share it with</h2>
      <p>We share personal data only in these situations:</p>
      <ul>
        <li>
          <strong>Our email delivery service.</strong> Enquiry form submissions
          are transmitted through a mail service operated on our behalf at
          services.mviaso.com, purely so the message reaches our inbox.
        </li>
        <li>
          <strong>Builders and developers.</strong> If you ask us to arrange a
          site visit, booking or unit hold, we pass on the details necessary to
          complete that request. We tell you when we are doing this.
        </li>
        <li>
          <strong>Professional advisers and authorities.</strong> Where required
          by law, by a court, or by a regulator with proper authority.
        </li>
      </ul>
      <p>
        We do not sell, rent or trade your personal data, and we do not share it
        with advertising networks.
      </p>

      <h2>5. Third-party services on this site</h2>
      <table>
        <thead>
          <tr>
            <th>Service</th>
            <th>Purpose</th>
            <th>Loaded when</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Google Fonts</td>
            <td>Website typography</td>
            <td>On every page</td>
          </tr>
          <tr>
            <td>Google Maps embed</td>
            <td>Shows the location of our office</td>
            <td>Only after you allow it in your cookie preferences</td>
          </tr>
          <tr>
            <td>Mviaso mail service</td>
            <td>Delivers your enquiry to our inbox</td>
            <td>Only when you submit the enquiry form</td>
          </tr>
        </tbody>
      </table>
      <p>
        These providers handle your data under their own privacy terms. Loading
        content from Google may involve a transfer of data outside India.
      </p>

      <h2>6. How long we keep it</h2>
      <p>
        Enquiries are retained for as long as needed to serve you, and generally
        for up to three years after our last contact, so that we can pick up a
        conversation you may return to. Where a transaction takes place, records
        are kept for the longer period required under applicable tax and
        contract law. You can ask us to erase your details sooner.
      </p>

      <h2>7. Your rights</h2>
      <p>
        Under the Digital Personal Data Protection Act, 2023 and applicable
        Indian law, you may ask us to:
      </p>
      <ul>
        <li>confirm what personal data of yours we hold and how we use it;</li>
        <li>correct or complete anything inaccurate or out of date;</li>
        <li>
          erase your data where we no longer need it for the purpose you gave it
          for;
        </li>
        <li>withdraw a consent you previously gave, at any time;</li>
        <li>nominate another person to exercise these rights on your behalf.</li>
      </ul>
      <p>
        Write to us using the details at the end of this page and we will
        respond within a reasonable period. If you are not satisfied with our
        response, you may escalate the matter to the Data Protection Board of
        India.
      </p>

      <h2>8. Security</h2>
      <p>
        The site is served over HTTPS and enquiry data is transmitted over an
        encrypted connection. Access to enquiry records is limited to the people
        in our team who need it. No method of transmission or storage is
        completely secure, so we cannot guarantee absolute security, but we take
        reasonable steps proportionate to the data we hold.
      </p>

      <h2>9. Children</h2>
      <p>
        This website is meant for adults considering property transactions. We
        do not knowingly collect personal data from children. If you believe a
        child has provided us with information, contact us and we will remove
        it.
      </p>

      <h2>10. Property information disclaimer</h2>
      <p>
        Project details, prices, layouts and approval information shown on this
        site are provided by the respective builders and are believed to be
        reliable, but they can change without notice and are not a substitute
        for the sanctioned plans, RERA registration entry or agreement documents.
        Please verify independently before making a commitment.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this policy as our services or the law change. The date at
        the top of this page always reflects the current version. Material
        changes affecting how we use your data will be highlighted on the site.
      </p>
    </LegalSection>
  </div>
</template>

<script setup>
import PageBanner from '@/components/PageBanner.vue';
import LegalSection from '@/components/LegalSection.vue';
import { site } from '@/data/site';
</script>

<style scoped>
.legal-page {
  overflow-x: hidden;
}
</style>
